// hooks/useVideoSlideController.js - WITH IMAGE UPLOAD - FIXED UPDATE LOGIC
import { useState, useRef, useMemo, useEffect } from 'react';
import { savePresentation, updatePresentation } from '../firestoreService';
import { 
  getTemplatesList,
  renderSlideToCanvas,
  renderSlideBackgroundsOnly,
  renderSlidesWithImages,
  exportSlideToPPTX,
  exportAllSlidesToPPTX 
} from '../canvas/SlideCanvasRenderer';
import { compressImage } from '../utils/imageCompression';

export function useVideoSlideController({
  user,
  processedSlides,
  slideTemplates,
  slideImages,
  isProcessing,
  isFetchingImages,
  getSlideImageUrl,
  handleReset,
  audioUrl,
  setAudioUrl,
  originalAudioFile,
  changeSlideTemplate,
  transcribeAudio,
  generateFromPrompt,
  generateMP4Video,
  setProcessedSlides,
  setSlideTemplates,
  setSlideImages,
}) {
  // ============ STATE ============
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [uploadMode, setUploadMode] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [audioFileName, setAudioFileName] = useState('');
  const [audioDuration, setAudioDuration] = useState(0);
  const [userPrompt, setUserPrompt] = useState('');
  const [showMP4Modal, setShowMP4Modal] = useState(false);
  const [showPNGModal, setShowPNGModal] = useState(false);
  const [isExportingPPTX, setIsExportingPPTX] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedPresentationId, setSavedPresentationId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isLoadingPresentation, setIsLoadingPresentation] = useState(false);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState({});
  const [presentationTitle, setPresentationTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [slideLayoutFilters, setSlideLayoutFilters] = useState({});
  const [audioExpiresAt, setAudioExpiresAt] = useState(null);

  // ============ REFS ============
  const hasSavedRef = useRef(false);
  const canvasRefs = useRef({});
  const audioInputRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const layoutFiltersInitialized = useRef(false);

  // ============ COMPUTED VALUES ============
  const templatesList = getTemplatesList();

  // Filter templates based on search term only
  const filteredTemplatesList = useMemo(() => {
    if (!searchTerm.trim()) return templatesList;
    
    const search = searchTerm.toLowerCase();
    return templatesList.filter(template => 
      template?.name?.toLowerCase().startsWith(search)
    );
  }, [searchTerm, templatesList]);

  // Get filtered templates for a specific slide based on layout filter
  const getFilteredTemplatesForSlide = (slideIndex) => {
    const layoutFilter = slideLayoutFilters[slideIndex] || 'right';
    let filtered = templatesList;
    
    // Filter by layout type
    if (layoutFilter === 'left') {
      filtered = filtered.filter(t => t.id.endsWith('_left'));
    } else if (layoutFilter === 'none') {
      filtered = filtered.filter(t => t.id.endsWith('_none'));
    } else {
      // 'right' - templates ending with _right OR no suffix
      filtered = filtered.filter(t => 
        t.id.endsWith('_right') || 
        (!t.id.endsWith('_left') && !t.id.endsWith('_none') && !t.id.endsWith('_right'))
      );
    }
    
    // Fallback: if no templates match, return all
    if (filtered.length === 0) {
      filtered = templatesList;
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(template => 
        template?.name?.toLowerCase().startsWith(search)
      );
    }
    
    return filtered;
  };

  // ============ UTILITY FUNCTIONS ============
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getLoadingMessage = () => {
    if (!fontsLoaded) {
      return { message: "Loading fonts...", submessage: "Preparing your workspace" };
    }
    if (isLoadingPresentation) {
      return { message: "Loading presentation...", submessage: "Just a moment" };
    }
    if (isExportingPPTX) {
      return { message: "Exporting to PowerPoint...", submessage: "Creating your presentation file" };
    }
    return null;
  };

  const getCurrentTemplate = (slideIndex) => {
    const templateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    return templatesList.find(t => t.id === templateId) || templatesList[0];
  };

  const getLayoutFilter = (slideIndex) => {
    return slideLayoutFilters[slideIndex] || 'right';
  };

  // ============ EVENT HANDLERS ============
  const onAudioChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadMode('audio');
      setAudioFile(file);
      setAudioFileName(file.name);
      
      const url = URL.createObjectURL(file);
      setAudioPreview(url);
    }
  };

  const handlePromptGenerate = async (prompt) => {
    setUploadMode('prompt');
    setUserPrompt(prompt);
    hasSavedRef.current = false;
    setSavedPresentationId(null);
    setPresentationTitle('');
    
    try {
      await generateFromPrompt(prompt, user?.uid || null);
    } catch (error) {
      
    }
  };

  const handleAudioGenerateWithPrompt = async (file, prompt) => {
    setUploadMode('audio');
    setAudioFile(file);
    setAudioFileName(file.name);
    setUserPrompt(prompt);
    
    const url = URL.createObjectURL(file);
    setAudioPreview(url);
    
    hasSavedRef.current = false;
    setSavedPresentationId(null);
    setPresentationTitle('');
    
    setHasStarted(true);
    
    try {
      await transcribeAudio(file, prompt);
    } catch (error) {
   
    }
  };

  const handleAudioProcess = async () => {
    if (audioFile) {
      hasSavedRef.current = false;
      setSavedPresentationId(null);
      await transcribeAudio(audioFile, userPrompt);
    }
  };

  const handleLoadPresentation = async (presentation) => {
    try {
      setIsLoadingPresentation(true);
      
      hasSavedRef.current = true;
      setSavedPresentationId(presentation.id);
      setSaveError(null);
      setPresentationTitle(presentation.title || 'Untitled');
      
      setUploadMode(presentation.uploadMode || 'prompt');
      setUserPrompt(presentation.originalPrompt || '');
      
      if (presentation.userUploadedFileName) {
  setAudioFileName(presentation.userUploadedFileName);
}
      
      
const loadedSlides = presentation.slides.map((slide, index) => ({
  extractedContent: slide.extractedContent,
  duration: slide.duration || null,  // ✅ RESTORE DURATION!
  startTime: slide.startTime || null,
  endTime: slide.endTime || null,
  audioSegments: slide.audioSegments || null,
  timestamp: null,
}));

      const loadedImages = presentation.imageUrls.map((url, index) => ({
        success: true,
        image: { url: url },
        slideIndex: index
      }));
      
      setProcessedSlides(loadedSlides);
      setSlideTemplates(presentation.templateIds || []);
      setSlideImages(loadedImages);
      
     // ✅ NEW CODE:
if (presentation.audioId) {
  setAudioUrl(presentation.audioId);
  setAudioExpiresAt(presentation.audioExpiresAt);

} else if (presentation.audioFileName) {
  // Backwards compatibility for old presentations
  setAudioUrl(presentation.audioFileName);
  setAudioExpiresAt(presentation.audioExpiresAt);

}
      
      // Initialize layout filters for loaded slides
      const initialFilters = {};
      for (let i = 0; i < loadedSlides.length; i++) {
        initialFilters[i] = i % 2 === 0 ? 'right' : 'left';
      }
      setSlideLayoutFilters(initialFilters);
      layoutFiltersInitialized.current = true;
      
      setHasStarted(true);
      
    } catch (error) {
     
      alert('Failed to load presentation. Please try again.');
    } finally {
      setIsLoadingPresentation(false);
    }
  };

  const handleCompleteReset = () => {
    handleReset();
    setHasStarted(false);
    setUploadMode(null);
    setAudioFile(null);
    setAudioPreview(null);
    setAudioFileName('');
    setAudioDuration(0);
    setUserPrompt('');
    setCurrentTemplateIndex({});
    setSearchTerm('');
    setPresentationTitle('');
    setSlideLayoutFilters({});
    layoutFiltersInitialized.current = false;
    hasSavedRef.current = false;
    setSavedPresentationId(null);
    setSaveError(null);
    setShowResults(false);
  };

  // Handle layout filter change
  const handleLayoutFilterChange = (slideIndex, newLayout) => {
  
    
    setSlideLayoutFilters(prev => ({
      ...prev,
      [slideIndex]: newLayout
    }));
    
    // Get the current template's base name (without suffix)
    const currentTemplateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    const baseName = currentTemplateId.replace(/_(left|none|right)$/, '');
    
    // Build the new template ID with the layout suffix
    let newTemplateId;
    if (newLayout === 'left') {
      newTemplateId = `${baseName}_left`;
    } else if (newLayout === 'none') {
      newTemplateId = `${baseName}_none`;
    } else {
      newTemplateId = `${baseName}_right`;
    }
    
    changeSlideTemplate(slideIndex, newTemplateId);
 
  };

  // ✅ FIXED: Handle custom image upload with update logic
 // ✅ MAXIMUM QUALITY: handleImageUpload with minimal compression
const handleImageUpload = async (slideIndex, file) => {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('Please select a valid image file');
    }

    // Validate file size - max 10MB
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('Image size must be less than 10MB');
    }

    

    // ✅ MAXIMUM QUALITY: Only compress if really needed
    const compressedFile = await compressImage(file, {
      maxSizeMB: 5,              // ✅ Only compress if > 5MB (was 3MB)
      maxWidthOrHeight: 2400,    // ✅ Higher res (was 1920)
      quality: 0.98,             // ✅ Near-lossless (was 0.95)
      preserveTransparency: true
    });

    

    // Upload to Cloudinary
    const formData = new FormData();
    formData.append('file', compressedFile);
    formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', 'slide_images');
    
    // ✅ NEW: Request high quality from Cloudinary
    formData.append('quality', 'auto:best');

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.secure_url) {
      throw new Error('No secure URL returned from Cloudinary');
    }

    // ✅ BEST QUALITY: Cloudinary transformations optimized for presentations
    // q_auto:best = Highest quality automatic optimization
    // f_auto = Best format (WebP if supported, falls back to original)
    // w_2400 = Max width 2400px (higher than before)
    // c_limit = Don't upscale, only downscale if needed
    const optimizedUrl = `${data.secure_url.replace('/upload/', '/upload/q_auto:best,f_auto,w_2400,c_limit/')}`;
    
    
    // Update slide images state with optimized URL
    setSlideImages(prev => {
      const updated = [...prev];
      updated[slideIndex] = {
        success: true,
        image: { url: optimizedUrl },
        slideIndex: slideIndex,
        isUserUploaded: true
      };
      return updated;
    });

    // ✅ If presentation is already saved, update it immediately
    if (savedPresentationId && user) {
      
      
      try {
        const updatedImageUrls = slideImages.map((img, idx) => {
          if (idx === slideIndex) {
            return optimizedUrl;
          }
          return img?.image?.url || null;
        });

        await updatePresentation(savedPresentationId, {
          imageUrls: updatedImageUrls
        });

      } catch (updateError) {
       
      }
    } else {
      hasSavedRef.current = false;
    }

   

  } catch (error) {
   
    throw error;
  }
};

  const handleTemplateSelect = (slideIndex, templateId) => {
    changeSlideTemplate(slideIndex, templateId);
  };

  const handleTemplateNext = (slideIndex) => {
    const filteredForSlide = getFilteredTemplatesForSlide(slideIndex);
    const currentTemplateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    const currentIdx = filteredForSlide.findIndex(t => t.id === currentTemplateId);
    
    if (currentIdx === -1) {
      if (filteredForSlide.length > 0) {
        changeSlideTemplate(slideIndex, filteredForSlide[0].id);
      }
      return;
    }
    
    const nextIdx = (currentIdx + 1) % filteredForSlide.length;
    setCurrentTemplateIndex(prev => ({ ...prev, [slideIndex]: nextIdx }));
    changeSlideTemplate(slideIndex, filteredForSlide[nextIdx].id);
  };

  const handleTemplatePrev = (slideIndex) => {
    const filteredForSlide = getFilteredTemplatesForSlide(slideIndex);
    const currentTemplateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    const currentIdx = filteredForSlide.findIndex(t => t.id === currentTemplateId);
    
    if (currentIdx === -1) {
      if (filteredForSlide.length > 0) {
        changeSlideTemplate(slideIndex, filteredForSlide[filteredForSlide.length - 1].id);
      }
      return;
    }
    
    const prevIdx = currentIdx === 0 ? filteredForSlide.length - 1 : currentIdx - 1;
    setCurrentTemplateIndex(prev => ({ ...prev, [slideIndex]: prevIdx }));
    changeSlideTemplate(slideIndex, filteredForSlide[prevIdx].id);
  };

  const handleDownloadSlidePPTX = async (slide, index) => {
    try {
      const canvas = document.createElement('canvas');
      const templateId = slideTemplates[index] || 'modern_bullets_right';
      const imageUrl = getSlideImageUrl(index);
      
      await renderSlideToCanvas(canvas, slide.extractedContent, templateId, imageUrl);
      
      const renderedSlideData = {
        imageData: canvas.toDataURL('image/png', 1.0)
      };
      
      await exportSlideToPPTX(
        slide.extractedContent, 
        templateId, 
        index,
        imageUrl,
        renderedSlideData
      );
      
    } catch (error) {

      alert('Failed to export slide to PPTX');
    }
  };

  const handleDownloadAllPPTX = async () => {
    if (isExportingPPTX) return;
    
    try {
      setIsExportingPPTX(true);
      

      
      const chartSlideIndices = processedSlides
        .map((slide, index) => slide.extractedContent?.chartData ? index : null)
        .filter(i => i !== null);
      
     
      
      const renderedBackgrounds = await renderSlideBackgroundsOnly(
        processedSlides,
        slideTemplates,
        slideImages
      );
      
      if (chartSlideIndices.length > 0) {
    
        
        for (const chartIndex of chartSlideIndices) {
          const canvas = document.createElement('canvas');
          const templateId = slideTemplates[chartIndex] || 'modern_bullets_right';
          const imageUrl = getSlideImageUrl(chartIndex);
          
          await renderSlideToCanvas(
            canvas,
            processedSlides[chartIndex].extractedContent,
            templateId,
            imageUrl
          );
          
          renderedBackgrounds[chartIndex] = {
            slideIndex: chartIndex,
            imageData: canvas.toDataURL('image/png', 1.0),
            template: templateId,
            isChartSlide: true,
            hasChart: true,
            hasImage: false
          };
          
          
        }
      }
      
      
      
      await exportAllSlidesToPPTX(
        processedSlides,
        slideTemplates,
        slideImages,
        renderedBackgrounds
      );
      
     
      
    } catch (error) {
      
      alert(`Failed to export PPTX: ${error.message}`);
    } finally {
      setIsExportingPPTX(false);
    }
  };

 const handleMP4Export = async (options) => {
  try {
    const finalOptions = {
      ...options,
      includeAudio: options.includeAudio,
      duration: options.duration || 3, // ✅ FIX: Include duration with default
    };

   

    await generateMP4Video(finalOptions, user?.uid || null);
    setShowMP4Modal(false);
  } catch (error) {
   
  }
};
  // ============ EFFECTS ============
  useEffect(() => {
    if (audioPreview || uploadMode === 'prompt') {
      setHasStarted(true);
    }
  }, [audioPreview, uploadMode]);

  useEffect(() => {
    if (processedSlides.length > 0 && !showResults) {
      setTimeout(() => {
        setShowResults(true);
      }, 100);
    } else if (processedSlides.length === 0 && showResults) {
      setShowResults(false);
    }
  }, [processedSlides.length, showResults]);

  // Initialize layout filters
  useEffect(() => {
    if (processedSlides.length > 0 && !layoutFiltersInitialized.current) {
      const initialFilters = {};
      for (let i = 0; i < processedSlides.length; i++) {
        initialFilters[i] = i % 2 === 0 ? 'right' : 'left';
      }
      setSlideLayoutFilters(initialFilters);
      layoutFiltersInitialized.current = true;
    }
  }, [processedSlides.length]);

  useEffect(() => {
    if (processedSlides.length > 0 && userPrompt && !presentationTitle) {
      const generatedTitle = userPrompt.trim().substring(0, 40);
      setPresentationTitle(generatedTitle);
    }
  }, [processedSlides.length, userPrompt, presentationTitle]);

  // ✅ UPDATED: Auto-save effect with update logic
  useEffect(() => {
    const autoSavePresentation = async () => {
      // Only proceed if we have user, slides, and not currently processing
      if (
        !user || 
        processedSlides.length === 0 || 
        isProcessing || 
        isFetchingImages ||
        isLoadingPresentation
      ) {
        return;
      }

      try {
        // ✅ CASE 1: Presentation already exists - UPDATE IT
        if (savedPresentationId && !hasSavedRef.current) {
          
          setIsSaving(true);
          setSaveError(null);

          // Prepare image URLs
          const imageUrls = slideImages.map(img => img?.image?.url || null);

          await updatePresentation(savedPresentationId, {
            slides: processedSlides.map(slide => ({
              extractedContent: slide.extractedContent,
              duration: slide.duration || 5,
              startTime: slide.startTime || null,
              endTime: slide.endTime || null,
              audioSegments: slide.audioSegments || null
            })),
            templateIds: slideTemplates || [],
            imageUrls: imageUrls || [],
            slideCount: processedSlides.length,
            audioFileName: audioUrl || null,
            audioExpiresAt: audioExpiresAt || null,
            hasAudioSync: !!audioUrl,
            userUploadedFileName: audioFileName || null 
          });

          hasSavedRef.current = true;
     
        }
        // ✅ CASE 2: No presentation exists - CREATE NEW ONE
        else if (!savedPresentationId && !hasSavedRef.current) {
        
          setIsSaving(true);
          setSaveError(null);
          hasSavedRef.current = true;
          
          const presentationData = {
            userId: user.uid,
            userEmail: user.email,
            uploadMode,
            userPrompt,
            processedSlides,
            slideTemplates,
            slideImages,
            audioFileName: audioUrl || null,
            audioExpiresAt: audioExpiresAt || null,
            userUploadedFileName: audioFileName || null 
          };
          
          const docId = await savePresentation(presentationData);
          setSavedPresentationId(docId);
         
        }
        
      } catch (error) {
      
        setSaveError(error.message);
        hasSavedRef.current = false;
      } finally {
        setIsSaving(false);
      }
    };

    autoSavePresentation();
  }, [
    user, 
    processedSlides, 
    isProcessing, 
    isFetchingImages,
    isLoadingPresentation,
    uploadMode,
    userPrompt,
    slideTemplates,
    slideImages,
    savedPresentationId,
    audioUrl,
    audioExpiresAt
  ]);

  // ============ RETURN ALL STATE AND HANDLERS ============
  return {
    // State
    fontsLoaded,
    setFontsLoaded,
    hasStarted,
    uploadMode,
    audioFile,
    audioUrl, 
    audioPreview,
    audioFileName,
    audioDuration,
    setAudioDuration,
    userPrompt,
    showMP4Modal,
    setShowMP4Modal,
    showPNGModal,
    setShowPNGModal,
    isExportingPPTX,
    showResults,
    isSaving,
    savedPresentationId,
    saveError,
    isLoadingPresentation,
    currentTemplateIndex,
    presentationTitle,
    searchTerm,
    setSearchTerm,
    slideLayoutFilters,
    audioExpiresAt,
    
    // Refs
    canvasRefs,
    audioInputRef,
    audioPlayerRef,
    
    // Computed
    templatesList,
    filteredTemplatesList,
    getFilteredTemplatesForSlide,
    getLayoutFilter,
    
    // Utilities
    formatTime,
    getLoadingMessage,
    getCurrentTemplate,
    
    // Handlers
    onAudioChange,
    handlePromptGenerate,
    handleAudioGenerateWithPrompt,
    handleAudioProcess,
    handleLoadPresentation,
    handleCompleteReset,
    handleTemplateSelect,
    handleTemplateNext,
    handleTemplatePrev,
    handleDownloadSlidePPTX,
    handleDownloadAllPPTX,
    handleMP4Export,
    handleLayoutFilterChange,
    handleImageUpload,
  };
}